// src/utils/invitationRules.js
// Pure rules shared by the invitation screens (Roadmap #54, Invitations Build 2 Phase 3;
// docs/briefs/2026-10-01-group-invitations-build-2.md + addenda 1 and 2). No data access here.
//
// Both rules are copies of database rules and must stay in step with them (docs/reuse-backlog.md):
// 1. isPlainEmail — public._is_plain_email (20261003c) and supabase/functions/_shared/email.ts
//    isSingleBareAddress: one ASCII local@domain, at most 254 characters, exactly one @. The
//    database answers 'invalid_address' for anything else; checking here first lets the panel say
//    which addresses before anything is created.
// 2. pickGroupPlace — public._group_place (20261003c), the canonical rule: the first 200 areas;
//    none found → ''; one found → its name; all found share one non-NULL parent with a name → that
//    parent's name; else the first area, in the group's own order, that exists.

const PLAIN_ADDRESS = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;

export function isPlainEmail(value) {
  return typeof value === 'string'
    && value.length <= 254
    && value.split('@').length === 2
    && PLAIN_ADDRESS.test(value);
}

export const GROUP_PLACE_MAX_AREAS = 200;

/**
 * @param {Array<number|string>} boundaryIds — the group's boundary_ids, in their own order
 * @param {Array<{id, name, parent_id}>} rows — admin_boundaries rows found for the first 200 of them
 * @param {(parentId: number) => string|null} parentName — the name of a parent boundary, if known
 * @returns {string} the place, or '' when none is found
 */
export function pickGroupPlace(boundaryIds, rows, parentName) {
  const ids = (boundaryIds || []).slice(0, GROUP_PLACE_MAX_AREAS).map(Number);
  if (ids.length === 0) return '';
  const byId = new Map();
  (rows || []).forEach(r => { if (ids.includes(Number(r.id))) byId.set(Number(r.id), r); });
  const found = [...byId.values()];
  if (found.length === 0) return '';
  if (found.length === 1) return found[0].name || '';
  const parents = new Set(found.map(r => (r.parent_id == null ? null : Number(r.parent_id))));
  if (!parents.has(null) && parents.size === 1) {
    const name = parentName([...parents][0]);
    if (name) return name;
  }
  const first = ids.find(id => byId.has(id));
  return byId.get(first)?.name || '';
}

/**
 * accept_invitation answers in English (its 20260922 shape, kept); the page shows translated codes
 * (addendum 1 item 5). 'Already a member' comes back with success=true and is its own code, so the
 * page does not say "welcome" to someone already in.
 * @returns {'joined'|'already_member'|'not_authenticated'|'not_found'|'already_used'|'revoked'|
 *           'expired'|'group_unavailable'|'blocked'|'failed'}
 */
export function acceptInvitationCode(result) {
  if (result?.success) return result.error_message === 'Already a member' ? 'already_member' : 'joined';
  switch (result?.error_message) {
    case 'Not authenticated':
    case 'Caller mismatch': return 'not_authenticated';
    case 'Invitation not found': return 'not_found';
    case 'Invitation already used': return 'already_used';
    case 'Invitation has been revoked': return 'revoked';
    case 'Invitation has expired': return 'expired';
    case 'Group is no longer available': return 'group_unavailable';
    case 'You cannot join this group': return 'blocked';
    default: return 'failed';
  }
}

/**
 * What the invite panel tells the admin about one address's email, from send-invitation's outcome
 * (supabase/functions/send-invitation/index.ts header). 'not_attempted' is the panel's own: a batch
 * never reached the function. Anything unknown counts as failed — the link still works.
 * @returns {'emailed'|'opted_out'|'limit'|'already_emailed'|'in_progress'|'failed'}
 */
export function sendOutcomeGroup(outcome) {
  switch (outcome) {
    case 'sent': return 'emailed';
    case 'suppressed': return 'opted_out';
    case 'sender_budget':
    case 'group_budget':
    case 'platform_budget': return 'limit';
    case 'already_sent': return 'already_emailed';
    case 'already_reserved': return 'in_progress';
    default: return 'failed';
  }
}

/** The single shared parent of the found rows, if they have one — the one extra read the rule needs. */
export function sharedParentId(rows) {
  const parents = new Set((rows || []).map(r => (r.parent_id == null ? null : Number(r.parent_id))));
  return (rows || []).length > 1 && !parents.has(null) && parents.size === 1 ? [...parents][0] : null;
}
